import { infiniteQueryOptions, queryOptions, type QueryClient } from "@tanstack/react-query";
import { getBlockList, getCoinBalance, getCollectionRobot, getCollectionRobotCategories, getCommentList, getFollowerList, getFollowingList, getOtherUserRobots, getUserInfoById, getUserRobot, getUserRobotCategories, vipExpired } from "../profile";
import { PageData } from "../type";
import { getPersonaList } from "../persona";
import { USER_STORE_KEY } from "@/store/useUserStore";

export const profileRobotQueryOptions = ({
  filter = "0",
  category = 'All'
}: {
  filter?,
  category?: string
} = {}) => infiniteQueryOptions({
  queryKey: ['profile', 'robots', filter, category],
  initialPageParam: { page: 1, limit: 20 },
  queryFn: async ({ pageParam }): Promise<PageData> => {
    const data: any = {}
    if (category !== 'All') {
      data.category_id = category
    }
    switch (filter) {
      case "1":
        data.is_use = true
        break
      case "2":
        data.audit_status = 10
        break
      case "3":
        data.audit_status = 20
        break
      case "4":
        data.report_status = 30
    }
    // 不能 Object.assign(pageParam)：会改掉 React Query 缓存的 pageParam
    return (await getUserRobot({ ...pageParam, ...data })).data
  },
  getNextPageParam: (lastPage, _allPages, lastPageParam) => {
    const list = lastPage?.data
    const limit = lastPageParam?.limit ?? 20
    // 空页 / 不足一页：一定没有下一页。不能只靠 last_page === current_page：
    // v1.0 userRobots 经常 last_page 缺失/为 0、current_page 恒为 1，
    // 或 "2" === 2 为 false，hasNextPage 会永远为 true，ScrollLoadList 就会连打。
    if (!list?.length || list.length < limit) return null
    if (
      lastPage.last_page != null &&
      lastPage.current_page != null &&
      Number(lastPage.last_page) === Number(lastPage.current_page)
    ) {
      return null
    }
    return { limit, page: (lastPageParam?.page ?? 1) + 1 }
  },
})

// 人设列表
export const ProfilePersonalListQueryOptions = infiniteQueryOptions({
  queryKey: ['profile', 'persona', 'list'],
  initialPageParam: { page: 1, limit: 20 },
  queryFn: async ({ pageParam }): Promise<any> => {
    const data = (await getPersonaList(pageParam)).data
    return data
  },
  getNextPageParam: (lastPage, allPages, lastPageParam) => {
    if (!lastPage || !lastPage.data || lastPage?.data.length === 0) return null
    return { ...lastPageParam, page: lastPageParam.page + 1 }
  },
})


// 用户的机器人分类列表
export const ProfileRobotCategoriesQueryOptions = queryOptions({
  queryKey: ['profile', 'robots', 'categories'],
  queryFn: getUserRobotCategories
})

// flolower 列表
// 后端 /Api/Follower/attentions 使用 last_id 游标分页（where f.id < last_id order by f.id desc），
// 不识别 page 参数，因此必须基于上一页最后一条的 id 作为游标，否则会一直返回第一页。
export const followerListQueryOptions = ({ id = "profile" }) => infiniteQueryOptions({
  queryKey: ['get', 'follower', id],
  initialPageParam: { page: 1, limit: 15, find_user_id: id, last_id: "" },
  queryFn: async ({ pageParam }): Promise<PageData> => {
    const params = { ...pageParam } as any
    if (id === 'profile')
      delete params.find_user_id
    if (!params.last_id)
      delete params.last_id
    return (await getFollowerList(params) as any).data
  },
  getNextPageParam: (lastPage: any, allPages, lastPageParam: any) => {
    if (!lastPage?.length) return null
    return { ...lastPageParam, page: lastPageParam.page + 1, last_id: lastPage[lastPage.length - 1].id }
  }
})

// following 列表
export const followingListQueryOptions = ({ id = "profile", userId }) => infiniteQueryOptions({
  queryKey: ['get', 'following', id],
  initialPageParam: { page: 1, limit: 15, find_user_id: id, last_id: "" },
  queryFn: async ({ pageParam }): Promise<PageData> => {
    const params = { ...pageParam } as any
    if (id === 'profile')
      delete params.find_user_id
    if (!params.last_id)
      delete params.last_id
    params.user_id = userId
    return (await getFollowingList(params) as any).data
  },
  getNextPageParam: (lastPage: any, allPages, lastPageParam) => {
    if (!lastPage?.length) return null
    return { ...lastPageParam, page: lastPageParam.page + 1, last_id: lastPage[lastPage!.length - 1].id }
  }
})

// 拉黑列表
export const BlockUserListQueryOptions = infiniteQueryOptions({
  queryKey: ['profile', 'blocklist'],
  queryFn: async ({ pageParam }) => {
    return (await getBlockList({ ...pageParam }) as any).data
  },
  initialPageParam: { page: 1, limit: 15 },
  getNextPageParam: (lastPage, allPages, lastPageParam) => {
    if (!lastPage?.length) return null
    return { limit: 15, page: lastPageParam.page + 1 }
  }
})

// 评论列表
export const ProfileCommentListQueryOptions = infiniteQueryOptions({
  queryKey: ['profile', 'commentList'],
  queryFn: async ({ pageParam }) => {
    return (await getCommentList({ ...pageParam }) as any).data
  },
  initialPageParam: { page: 1, limit: 15 },
  getNextPageParam: (lastPage, allPages, lastPageParam) => {
    if (!lastPage?.length) return null
    return { limit: 15, page: lastPageParam.page + 1 }
  }
})

// 收藏机器人列表
export const profileCollectionListQueryOptions = ({ categories }) => infiniteQueryOptions({
  queryKey: ['profile', 'collection', categories],
  queryFn: async ({ pageParam }) => {
    const params = { ...pageParam }
    if (categories === 'all')
      delete params.categories
    return (await getCollectionRobot({ ...params, }) as any).data.list
  },
  initialPageParam: { page: 1, page_size: 15, categories },
  getNextPageParam: (lastPage, allPages, lastPageParam) => {
    if (!lastPage?.length) return null
    return { ...lastPageParam, page: lastPageParam.page + 1 }
  },
  staleTime: 0
})

// 用户收藏的机器人标签列表
export const ProfileCollectionTagQueryOptions = queryOptions({
  queryKey: ['profile', 'collection', 'tags'],
  queryFn: async () => (await getCollectionRobotCategories()).data
})


// 用户信息
// 注意：底层 userInformation 接口需要登录(JwtAuth)，未登录/过期会返回 102。
// 该信息属于公开页的「补充内容」，因此不重试、不向上抛错，
// 避免在公开页因 token 缺失/过期而触发全局错误边界把用户引导去登录。
// 「其他用户」信息查询。底层 userInformation 需鉴权(JwtAuth)，未登录/过期会 102。
// 行为集中在此一处，调用方无需各自判断登录态：
//   1) 客户端无 token 直接返回空，不发必失败的鉴权请求；
//   2) 即便出错也吞掉返回空(成功态)，避免公开页触发全局登录跳转；
//   3) staleTime: Infinity —— 查看的是「他人」信息，会话内基本不变，
//      既消除重挂载请求风暴，也保护服务端 seed 的公开名称/头像不被空结果覆盖。
export const userInfoQueryOptions = ({ userId, headers = {} }) => queryOptions({
  queryKey: ['user', 'info', String(userId)],
  queryFn: async () => {
    // 空结果统一返回 null：调用方普遍用 `data?.data.x`(仅守卫最外层)，
    // 返回 null 可让整条可选链短路，避免 data 为 {data:null} 时读属性崩溃。
    if (typeof window !== 'undefined') {
      try {
        const raw = window.localStorage.getItem(USER_STORE_KEY)
        const hasToken = !!(raw && JSON.parse(raw)?.state?.token)
        if (!hasToken) return null as any
      } catch (e) { /* localStorage 解析失败按未登录处理 */ }
    }
    try {
      return await getUserInfoById({ find_user_id: Number(userId) }, { headers })
    } catch (e) {
      return null as any
    }
  },
  retry: false,
  throwOnError: false,
  staleTime: Infinity,
  refetchOnWindowFocus: false,
})

// 用户其他机器人
export const userOtherRobotsQueryOptions = ({ userId }) => infiniteQueryOptions({
  queryKey: ['user', userId, 'robots'],
  initialPageParam: { page: 1, limit: 20, find_user_id: userId },
  queryFn: async ({ pageParam }) => {
    const data = (await getOtherUserRobots({
      ...pageParam,
    }, { cache: "force-cache", next: { revalidate: 60 * 60 } }
    )).data
    return data
  },
  getNextPageParam: (lastPage, allPages, lastPageParam) => {
    if (lastPage?.last_page === lastPage?.current_page) return null
    return {
      ...lastPageParam,
      page: lastPageParam.page + 1,
    }
  },
})


// 获取vip的过期时间
export const VipExpiredQueryOptions = queryOptions({
  queryKey: ['vip', 'expired'],
  queryFn: vipExpired
})

export type CoinBalance = {
  coin: number
  starCoin: number
}

export const COIN_BALANCE_QUERY_KEY = ['user', 'coin-balance'] as const

const readCoinBalance = (data: {
  energy_coin?: number
  coin?: number
  start_coin?: number
  data?: { energy_coin?: number; coin?: number; start_coin?: number }
} | null | undefined): CoinBalance => {
  const nested = data?.data
  const payload = nested && typeof nested === 'object' ? nested : data
  const coin = Number(payload?.energy_coin ?? payload?.coin)
  const starCoin = Number(payload?.start_coin)
  return {
    coin: Number.isFinite(coin) ? coin : 0,
    starCoin: Number.isFinite(starCoin) ? starCoin : 0,
  }
}

export const coinBalanceQueryOptions = (userId: string | number) => queryOptions({
  queryKey: [...COIN_BALANCE_QUERY_KEY, String(userId)],
  queryFn: async () => {
    const res = await getCoinBalance()
    if (Number(res?.status) !== 200 || res?.data == null) {
      throw new Error(res?.message || 'coin balance')
    }
    return readCoinBalance(res.data)
  },
  // 全局 staleTime 是 5 分钟。余额会随聊天、生成变化，新挂载的观察者需要重新拉；进行中的请求仍合并成一次。
  staleTime: 0,
  retry: false,
  throwOnError: false,
})

export const invalidateCoinBalance = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: COIN_BALANCE_QUERY_KEY })

export const setCoinBalance = (queryClient: QueryClient, patch: Partial<CoinBalance>) => {
  queryClient.setQueriesData<CoinBalance>({ queryKey: COIN_BALANCE_QUERY_KEY }, (old) => {
    if (!old) return old
    return {
      coin: patch.coin ?? old.coin,
      starCoin: patch.starCoin ?? old.starCoin,
    }
  })
}